// src/modules/panelGlobal/pages/PanelGlobalPage.jsx
import React, { useEffect, useState } from "react";
import {
  Box, Paper, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, CircularProgress, Alert, Typography,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import {
  listPanelGlobal,
  postSincronizarWebTrsRastreo,
  postSincronizarWialonLeasecontrol,
} from "../services/panelGlobalService";
import PanelGlobalDetailDialog from "../components/PanelGlobalDetailDialog";
import ReporteEquiposSinFacturarDialog from "../components/ReporteEquiposSinFacturarDialog";

const headCellSx = {
  fontWeight: 800,
  py: 0.5,
  fontSize: "0.72rem",
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};

const bodyCellSx = { py: 0.4, fontSize: "0.8rem" };

const PLATAFORMA_LABEL = {
  wialon: { label: "Wialon", color: "info" },
  leasecontrol: { label: "Leasecontrol", color: "secondary" },
  trsrastreo: { label: "TRS Rastreo", color: "success" },
};

const OPCIONES_FILTRO_PLATAFORMA = [
  { value: "", label: "Todas" },
  { value: "wialon", label: "Wialon" },
  { value: "leasecontrol", label: "Leasecontrol" },
  { value: "trsrastreo", label: "TRS Rastreo" },
  { value: "sin_plataforma", label: "Sin plataforma" },
];

const OPCIONES_FILTRO_REVISION = [
  { value: "", label: "Todas" },
  { value: "Concluido", label: "Concluido" },
  { value: "Pendiente", label: "Pendiente" },
  { value: "sin_revisar", label: "Sin revisar" },
];

const compactFieldSx = {
  minWidth: 150,
  "& .MuiInputBase-root": { fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.7 },
  "& .MuiSelect-select": { py: "6.5px !important" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

function PlataformaChip({ plataforma }) {
  if (!plataforma) {
    return <Chip size="small" variant="outlined" label="Sin plataforma" />;
  }
  const cfg = PLATAFORMA_LABEL[plataforma] || { label: plataforma, color: "default" };
  return <Chip size="small" color={cfg.color} label={cfg.label} />;
}

function RevisionChip({ estado }) {
  if (estado === "Concluido") return <Chip size="small" color="success" label="Concluido" />;
  if (estado === "Pendiente") return <Chip size="small" color="warning" label="Pendiente" />;
  return <Chip size="small" variant="outlined" label="Sin revisar" />;
}

export default function PanelGlobalPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [buscar, setBuscar] = useState("");
  const [buscarDebounced, setBuscarDebounced] = useState("");
  const [plataformaFiltro, setPlataformaFiltro] = useState("");
  const [revisionFiltro, setRevisionFiltro] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [detalleAbierto, setDetalleAbierto] = useState(null);

  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [syncResultado, setSyncResultado] = useState(null);
  const [syncError, setSyncError] = useState("");

  const [confirmSyncWlOpen, setConfirmSyncWlOpen] = useState(false);
  const [sincronizandoWl, setSincronizandoWl] = useState(false);
  const [syncWlResultado, setSyncWlResultado] = useState(null);
  const [syncWlError, setSyncWlError] = useState("");

  const [reportePlataformaAbierta, setReportePlataformaAbierta] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscarDebounced(buscar), 400);
    return () => clearTimeout(t);
  }, [buscar]);

  const fetchLista = React.useCallback(() => {
    let cancelado = false;
    setLoading(true);
    setError("");
    listPanelGlobal({
      buscar: buscarDebounced,
      plataforma: plataformaFiltro,
      revision: revisionFiltro,
      page: page + 1,
      limit: rowsPerPage,
    })
      .then(({ data: resp }) => {
        if (cancelado) return;
        setData(resp.data || []);
        setTotal(resp.total || 0);
      })
      .catch((e) => {
        if (cancelado) return;
        setError(e?.response?.data?.error || "No se pudo cargar el panel global.");
        setData([]);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => { cancelado = true; };
  }, [buscarDebounced, plataformaFiltro, revisionFiltro, page, rowsPerPage]);

  useEffect(() => fetchLista(), [fetchLista]);

  const handleSincronizarTrs = async () => {
    setConfirmSyncOpen(false);
    setSincronizando(true);
    setSyncError("");
    setSyncResultado(null);
    try {
      const { data: resultado } = await postSincronizarWebTrsRastreo();
      setSyncResultado(resultado);
      fetchLista();
    } catch (e) {
      setSyncError(e?.response?.data?.error || "No se pudo sincronizar TRS Rastreo.");
    } finally {
      setSincronizando(false);
    }
  };

  const handleSincronizarWl = async () => {
    setConfirmSyncWlOpen(false);
    setSincronizandoWl(true);
    setSyncWlError("");
    setSyncWlResultado(null);
    try {
      const { data: resultado } = await postSincronizarWialonLeasecontrol();
      setSyncWlResultado(resultado);
      fetchLista();
    } catch (e) {
      setSyncWlError(e?.response?.data?.error || "No se pudo sincronizar Wialon/Leasecontrol.");
    } finally {
      setSincronizandoWl(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Panel Global"
        subtitle="Clientes con servicio AVL y en qué plataforma de rastreo están (Wialon, Leasecontrol, TRS Rastreo)"
        backTo="/dashboard"
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ p: 1.25, mb: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              label="Buscar cliente / código / servicio…"
              value={buscar}
              onChange={(e) => { setBuscar(e.target.value); setPage(0); }}
              sx={{ ...compactFieldSx, flex: "1 1 240px" }}
            />
            <FormControl size="small" sx={{ ...compactFieldSx, minWidth: 150, flex: "0 0 auto" }}>
              <InputLabel>Plataforma</InputLabel>
              <Select
                label="Plataforma"
                value={plataformaFiltro}
                onChange={(e) => { setPlataformaFiltro(e.target.value); setPage(0); }}
              >
                {OPCIONES_FILTRO_PLATAFORMA.map((o) => (
                  <MenuItem key={o.value || "todas"} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ ...compactFieldSx, minWidth: 140, flex: "0 0 auto" }}>
              <InputLabel>Revisión</InputLabel>
              <Select
                label="Revisión"
                value={revisionFiltro}
                onChange={(e) => { setRevisionFiltro(e.target.value); setPage(0); }}
              >
                {OPCIONES_FILTRO_REVISION.map((o) => (
                  <MenuItem key={o.value || "todas"} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={sincronizando ? <CircularProgress size={16} /> : <SyncIcon />}
                disabled={sincronizando}
                onClick={() => setConfirmSyncOpen(true)}
                sx={{ whiteSpace: "nowrap", py: 0.5, flex: "0 0 auto" }}
              >
                Sincronizar TRS Rastreo
              </Button>
            )}
            {isAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={sincronizandoWl ? <CircularProgress size={16} /> : <SyncIcon />}
                disabled={sincronizandoWl}
                onClick={() => setConfirmSyncWlOpen(true)}
                sx={{ whiteSpace: "nowrap", py: 0.5, flex: "0 0 auto" }}
              >
                Sincronizar Wialon/Leasecontrol
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => setReportePlataformaAbierta("wialon")}
              sx={{ whiteSpace: "nowrap", py: 0.5, flex: "0 0 auto" }}
            >
              Equipos sin Facturar (Wialon)
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => setReportePlataformaAbierta("leasecontrol")}
              sx={{ whiteSpace: "nowrap", py: 0.5, flex: "0 0 auto" }}
            >
              Equipos sin Facturar (Leasecontrol)
            </Button>
          </Box>
        </Paper>

        {syncError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncError("")}>{syncError}</Alert>}
        {syncResultado && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncResultado(null)}>
            {syncResultado.actualizados} cliente(s) actualizado(s) — {syncResultado.total_usuarios_trsrastreo} usuario(s)
            de TRS Rastreo revisados, {syncResultado.clientes_distintos} cliente(s) distinto(s) encontrados.
          </Alert>
        )}

        {syncWlError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncWlError("")}>{syncWlError}</Alert>}
        {syncWlResultado && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncWlResultado(null)}>
            {syncWlResultado.vinculados} vinculado(s) nuevo(s), {syncWlResultado.reactivados} reactivado(s),{" "}
            {syncWlResultado.sin_cambios} sin cambios, {syncWlResultado.sin_presencia} sin presencia en Wialon/Leasecontrol,{" "}
            {syncWlResultado.pendientes} marcado(s) en revisión Pendiente (de {syncWlResultado.clientes_evaluados} clientes evaluados).
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Cliente</TableCell>
                <TableCell sx={headCellSx}>Descripción</TableCell>
                <TableCell sx={headCellSx}>Sucursal</TableCell>
                <TableCell sx={headCellSx} align="center">Vehículos</TableCell>
                <TableCell sx={headCellSx}>Servicio</TableCell>
                <TableCell sx={headCellSx}>Plataforma</TableCell>
                <TableCell sx={headCellSx} align="center">Usuarios</TableCell>
                <TableCell sx={headCellSx} align="center">Veh. Plataforma</TableCell>
                <TableCell sx={headCellSx} align="center">Revisión</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : !data.length ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center", py: 2 }}>
                      No se encontraron clientes.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow
                    key={row.cod_servicio_cliente}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => setDetalleAbierto(row)}
                  >
                    <TableCell sx={bodyCellSx}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>{row.razon_social}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.cod_cliente} · #{row.cod_servicio_cliente}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{row.descripcion || "—"}</TableCell>
                    <TableCell sx={bodyCellSx}>{row.sucursal_facturar || "—"}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Chip size="small" variant="outlined" label={row.vehiculos_count} />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{row.servicio || "—"}</TableCell>
                    <TableCell sx={bodyCellSx}><PlataformaChip plataforma={row.plataforma} /></TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      {row.plataforma ? (
                        <Chip size="small" variant="outlined" label={row.usuarios_count} />
                      ) : "—"}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      {row.vehiculos_plataforma_count === null || row.vehiculos_plataforma_count === undefined ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={row.vehiculos_plataforma_count !== row.vehiculos_count ? "warning" : "default"}
                          label={row.vehiculos_plataforma_count}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <RevisionChip estado={row.revision_estado} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[15, 30, 50, 100]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
        />
      </Box>

      <PanelGlobalDetailDialog
        cliente={detalleAbierto}
        onClose={() => { setDetalleAbierto(null); fetchLista(); }}
      />

      <ReporteEquiposSinFacturarDialog
        open={Boolean(reportePlataformaAbierta)}
        onClose={() => setReportePlataformaAbierta(null)}
        plataforma={reportePlataformaAbierta || "wialon"}
        labelPlataforma={reportePlataformaAbierta === "leasecontrol" ? "Leasecontrol" : "Wialon"}
      />

      <Dialog open={confirmSyncOpen} onClose={() => setConfirmSyncOpen(false)}>
        <DialogTitle>Sincronizar TRS Rastreo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Esto trae todos los usuarios de TRS Rastreo (rastreo.com.bo) y actualiza
            cliente_servicio.web para cada cliente encontrado, en un solo paso — sin
            hacerlo uno por uno. No modifica nada en la plataforma externa, solo la
            base de datos local.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSyncOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSincronizarTrs}>Sincronizar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSyncWlOpen} onClose={() => setConfirmSyncWlOpen(false)}>
        <DialogTitle>Sincronizar Wialon/Leasecontrol</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Para cada cliente AVL activo, busca en Wialon y Leasecontrol qué usuario
            tiene acceso a sus modems (por número de SIM/IMEI) y vincula ese usuario
            al cliente — igual que hacerlo a mano desde el detalle de cada cliente,
            pero para todos a la vez. No modifica nada en la plataforma externa,
            solo las vinculaciones locales.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Si varios usuarios de la misma plataforma ven los modems del cliente
            (ej. varios empleados), vincula a todos. Un mismo usuario también
            puede quedar vinculado a más de un cliente (ej. cuentas tipo
            cooperativa/sindicato que administran varios clientes con un solo
            login) — no se considera un conflicto. Solo deja al cliente en
            revisión <strong>Pendiente</strong> cuando sus modems aparecen repartidos entre
            Wialon y Leasecontrol a la vez, o si falla al intentar vincular.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSyncWlOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSincronizarWl}>Sincronizar</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
